import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { apiHatasiMi } from '../api/hata';
import { aydinlatmaOnayla, aydinlatmaSurumu, ben, sifreDegistir } from '../api/kimlik';
import { Alan, Dugme, HataKutusu, Kart } from '../bilesenler/temel';
import { oturumDeposuKullan } from '../durum/oturum-deposu';

/**
 * İLK GİRİŞ KISITLARI.
 *
 * İkisi çözülmeden `/kimlik/sifre-degistir` ve `/kimlik/aydinlatma-onay`
 * DIŞINDA hiçbir uç açılmaz; hepsi 403 döner. Sıra ÖNEMLİ: sunucu önce
 * şifreye, sonra aydınlatmaya bakıyor (`erisim.guard.ts`), bu ekran da aynı
 * sırayı izler — aksi halde kullanıcı aydınlatmayı onaylar, sonra yine
 * engellenir ve sebebini anlamaz.
 */
export function IlkGiris() {
  const kullanici = oturumDeposuKullan((d) => d.kullanici);

  if (!kullanici) return null;
  if (kullanici.sifreDegistirmeliMi) return <SifreDegistirme />;
  return <AydinlatmaOnayi />;
}

/* ------------------------------------------------------------------ */

function SifreDegistirme() {
  const [mevcutSifre, mevcutYaz] = useState('');
  const [yeniSifre, yeniYaz] = useState('');
  const [tekrar, tekrarYaz] = useState('');
  const dustu = oturumDeposuKullan((d) => d.dustu);

  const eslesmiyorMu = tekrar !== '' && yeniSifre !== tekrar;

  const gonder = useMutation({
    mutationFn: () => sifreDegistir(mevcutSifre, yeniSifre),
    /*
     * ŞİFRE DEĞİŞİNCE TÜM OTURUMLAR DÜŞER (veritabanı tetikleyicisi) ve
     * sunucu yenileme çerezini siler. Panelde kalmak her isteğin 401
     * almasına yol açardı; kullanıcı doğrudan giriş ekranına alınır.
     */
    onSuccess: () => dustu(),
  });

  const alanHatalari = apiHatasiMi(gonder.error) ? gonder.error.alanHatalari : {};

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <Kart
        baslik="Şifrenizi değiştirin"
        aciklama="Devam etmek için şifrenizi değiştirmeniz gerekiyor. Değişiklikten sonra yeniden giriş yapacaksınız."
        cocuk={
          <div className="space-y-4">
            <Alan
              etiket="Mevcut şifre"
              tur="password"
              deger={mevcutSifre}
              degisti={mevcutYaz}
              otomatikTamamla="current-password"
              hatalar={alanHatalari.mevcutSifre}
            />
            <Alan
              etiket="Yeni şifre"
              tur="password"
              deger={yeniSifre}
              degisti={yeniYaz}
              otomatikTamamla="new-password"
              hatalar={alanHatalari.yeniSifre}
              ipucu="Asgari uzunluk sunucu parametresiyle belirlenir."
            />
            <Alan
              etiket="Yeni şifre (tekrar)"
              tur="password"
              deger={tekrar}
              degisti={tekrarYaz}
              otomatikTamamla="new-password"
              hatalar={eslesmiyorMu ? ['Şifreler eşleşmiyor.'] : undefined}
            />

            <HataKutusu hata={gonder.error} />

            <Dugme
              tikla={() => gonder.mutate()}
              bekliyor={gonder.isPending}
              pasif={mevcutSifre === '' || yeniSifre === '' || eslesmiyorMu}
              cocuk="Şifreyi değiştir"
            />
          </div>
        }
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function AydinlatmaOnayi() {
  const kullaniciyiYaz = oturumDeposuKullan((d) => d.kullaniciyiYaz);
  const kullanici = oturumDeposuKullan((d) => d.kullanici);

  /*
   * Sürüm `/kimlik/aydinlatma` ucundan okunur. `/api/v1/yonetim/ayar` da
   * taşıyor ama o uç `PARAMETRE_YONET` istiyor ve `@IlkGirisSerbest` değil;
   * onaylamamış kullanıcı çağıramaz.
   */
  const surum = useQuery({
    queryKey: ['aydinlatma-surumu'],
    queryFn: aydinlatmaSurumu,
    staleTime: Infinity,
  });

  const onayla = useMutation({
    mutationFn: (s: string) => aydinlatmaOnayla(s),
    onSuccess: async () => {
      /*
       * Onay 204 döner; bayrak yanıtta gelmez. `/kimlik/ben` bayrakları HER
       * İSTEKTE veritabanından çözüyor (`kimlik.okuyucu.ts`), bu yüzden taze
       * değeri oradan alıyoruz. `/yenile` çağırmak da işe yarardı ama
       * gereksiz bir jeton rotasyonu başlatırdı.
       */
      const taze = await ben();
      if (!kullanici) return;

      kullaniciyiYaz({
        ...kullanici,
        sifreDegistirmeliMi: taze.sifreDegistirmeliMi,
        aydinlatmaOnayliMi: taze.aydinlatmaOnayliMi,
        atamalar: taze.atamalar,
      });
    },
  });

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <Kart
        baslik="KVKK Aydınlatma Metni"
        aciklama="Devam etmek için aydınlatma metnini okuyup onaylamanız gerekiyor."
        cocuk={
          <div className="space-y-4">
            <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
              <p>
                Bu sistemde kimlik, iletişim, özlük, mesai ve konum verileriniz 6698 sayılı Kişisel
                Verilerin Korunması Kanunu kapsamında, iş sözleşmesinin ifası ve yasal
                yükümlülüklerin yerine getirilmesi amacıyla işlenmektedir.
              </p>
              <p className="mt-2">
                Verileriniz yalnız yetkili personel tarafından, görev tanımlarıyla sınırlı biçimde
                görüntülenir. Ücret ve sağlık bilgileriniz ayrı yetkilerle korunur ve her erişim
                denetim kaydına yazılır.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Metin sürümü: {surum.data?.surum ?? '—'}
              </p>
            </div>

            <HataKutusu hata={surum.error ?? onayla.error} />

            <Dugme
              tikla={() => surum.data && onayla.mutate(surum.data.surum)}
              bekliyor={onayla.isPending}
              pasif={!surum.data}
              cocuk="Okudum, onaylıyorum"
            />
          </div>
        }
      />
    </div>
  );
}
